import { useState, useMemo } from "react";
import "./PriceCalculator.css";

const PHONE           = "919800040505";
const W3F_ACCESS_KEY  = "177b492a-0669-447d-9ce6-c84c01717997";

// ── PRICING RULES — change these values to update rates site-wide ──
const MAN_FIRST_HOUR  = 500;   // ₹ per man for 1st hour (minimum)
const MAN_EXTRA_RATE  = 100;   // ₹ per man per hour from 2nd hour onwards
const MIN_ORDER_VALUE = 1000;  // ₹ minimum total; show warning if order falls below

const SERVICES = [
  { id: "garmap",  name: "Garden Map & Plant Details",  hindi: "Bagicha Naksha",      icon: "🗺️", type: "area",    rate: 10,  desc: "Detailed garden layout map with plant placement & species info" },
  { id: "grass",   name: "Grass Cutting",               hindi: "Ghaas Kaati",         icon: "🌿", type: "area",    rate: 1,   desc: "Regular grass trimming & lawn levelling" },
  { id: "hedge",   name: "Hedge Cutting",               hindi: "Jhad Kaat",           icon: "✂️", type: "area",    rate: 1,   desc: "Shaping & trimming hedges and shrubs" },
  { id: "khaad",   name: "Fertilizer",                  hindi: "Khaad Dena",          icon: "🌱", type: "area",    rate: 3,  desc: "Organic & chemical fertilizer application" },
  { id: "pani",    name: "Watering",                    hindi: "Pani Dena",           icon: "💧", type: "area",    rate: 0.5,   desc: "Scheduled garden watering service" },
  { id: "weed",    name: "Weed Removal",                hindi: "Kharpatwaar Hatana",  icon: "🍃", type: "manhour", rate: null, desc: "Complete removal of weeds from lawn & beds" },
  { id: "pest",    name: "Pest Control",                hindi: "Kida Niyantran",      icon: "🛡️", type: "area",    rate: 3,  desc: "Eco-safe pest & disease management" },
  { id: "pruning", name: "Tree Pruning",                hindi: "Pedh Chhataai",       icon: "🌳", type: "manhour", rate: null, desc: "Expert pruning for healthy plant growth" },
  { id: "soil",    name: "Soil Treatment",              hindi: "Mitti Upchar",        icon: "🏔️", type: "area",    rate: 3,  desc: "pH balancing & soil enrichment" },
  { id: "lawn",    name: "Lawn Mowing",                 hindi: "Lawn Katai",          icon: "🌾", type: "area",    rate: 1,   desc: "Precision mowing & professional edging" },
  { id: "cleanup", name: "Garden Cleanup",              hindi: "Bagicha Safai",       icon: "🧹", type: "area",    rate: 1,   desc: "Deep seasonal cleaning of garden" },
  { id: "plant",   name: "Planting Service",            hindi: "Paudha Lagana",       icon: "🌺", type: "manhour", rate: null, desc: "Professional plant installation & care" }
];

const UNITS = [
  { value: "sqft",  label: "Sq Ft",  abbr: "ft",  factor: 1       },
  { value: "meter", label: "Meter",  abbr: "m",   factor: 10.764  },
  { value: "gaj",   label: "Gaj",    abbr: "gaj", factor: 9       },
];

function fmt(n) {
  return Math.round(n).toLocaleString("en-IN");
}

function buildWhatsApp(breakdown, sqFt, workers, hours, payable) {
  const list = breakdown.map(s => `  • ${s.name}`).join("\n");
  const area = sqFt > 0 ? `\nArea: ${fmt(sqFt)} sq ft` : "";
  const msg  =
    `Hi Garvaana Enterprise! 🌿\n\nI'd like a quote for:\n${list}${area}\n\nEstimate: ₹${fmt(payable)}\n\nPlease share the final price.`;
  return `https://wa.me/${PHONE}?text=${encodeURIComponent(msg)}`;
}

function buildQueryMessage(breakdown, sqFt, unit, workers, hours, payable) {
  const lines = breakdown
    .map(s =>
      s.type === "area"
        ? `• ${s.name}: ${fmt(sqFt)} sq ft × ₹${s.rate} = ₹${fmt(s.cost)}`
        : `• ${s.name}: ${workers} worker(s) × ${hours} hr(s) = ₹${fmt(s.cost)}`
    )
    .join("\n");
  const areaLine = sqFt > 0 ? `\nGarden Area: ${fmt(sqFt)} sq ft (in ${unit})` : "";
  return `Services Requested:\n${lines}${areaLine}\n\nEstimated Total: ₹${fmt(payable)}\n\nKindly share the final quote.`;
}

export default function PriceCalculator() {
  const [selected,  setSelected]  = useState(new Set());
  const [search,    setSearch]    = useState("");
  const [length,    setLength]    = useState("");
  const [width,     setWidth]     = useState("");
  const [unit,      setUnit]      = useState("sqft");
  const [workers,   setWorkers]   = useState("");
  const [hours,     setHours]     = useState("");
  const [quoteOpen,  setQuoteOpen]  = useState(false);
  const [popStep,    setPopStep]    = useState("actions"); // "actions" | "form" | "success"
  const [formData,   setFormData]   = useState({ name: "", email: "", phone: "" });
  const [formErr,    setFormErr]    = useState({});
  const [submitting, setSubmitting] = useState(false);

  const openPopup = () => { setPopStep("actions"); setFormData({ name:"", email:"", phone:"" }); setFormErr({}); setQuoteOpen(true); };
  const closePopup = () => setQuoteOpen(false);

  const validate = () => {
    const e = {};
    if (!formData.name.trim())                          e.name  = "Name is required";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) e.email = "Enter a valid email";
    if (!formData.phone.trim())                         e.phone = "Phone is required";
    return e;
  };

  const sendQuery = async () => {
    const e = validate();
    if (Object.keys(e).length) { setFormErr(e); return; }
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("access_key", W3F_ACCESS_KEY);
      fd.append("subject",    `Garvaana — Price Calculator Quote from ${formData.name}`);
      fd.append("from_name",  formData.name);
      fd.append("email",      formData.email);
      fd.append("phone",      formData.phone);
      fd.append("service",    breakdown.map(s => s.name).join(", ") || "—");
      fd.append("message",    buildQueryMessage(breakdown, sqFt, unit, workers, hours, payable));
      const res  = await fetch("https://api.web3forms.com/submit", { method: "POST", body: fd });
      const data = await res.json();
      setPopStep(data.success ? "success" : "error");
    } catch {
      setPopStep("error");
    } finally {
      setSubmitting(false);
    }
  };

  const filtered = useMemo(() =>
    SERVICES.filter(s =>
      s.name.toLowerCase().includes(search.toLowerCase()) ||
      s.hindi.toLowerCase().includes(search.toLowerCase()) ||
      s.desc.toLowerCase().includes(search.toLowerCase())
    ), [search]
  );

  const sqFt = useMemo(() => {
    const l = parseFloat(length) || 0;
    const w = parseFloat(width)  || 0;
    const factor = UNITS.find(u => u.value === unit)?.factor || 1;
    return l * w * factor;
  }, [length, width, unit]);

  // Hr 1 = ₹500/man; hr 2+ = ₹150/man/hr; returns 0 if either field is empty
  const manHourCost = useMemo(() => {
    const w = parseInt(workers) || 0;
    const h = parseInt(hours)   || 0;
    if (!w || !h) return 0;
    return w * (MAN_FIRST_HOUR + Math.max(0, h - 1) * MAN_EXTRA_RATE);
  }, [workers, hours]);

  const breakdown = useMemo(() =>
    SERVICES
      .filter(s => selected.has(s.id))
      .map(s => ({
        ...s,
        cost: s.type === "area" ? sqFt * s.rate : manHourCost,
      })),
    [selected, sqFt, manHourCost]
  );

  const total = useMemo(() =>
    breakdown.reduce((sum, s) => sum + s.cost, 0), [breakdown]
  );

  const belowMinimum = breakdown.length > 0 && total < MIN_ORDER_VALUE;
  const payable      = belowMinimum ? MIN_ORDER_VALUE : total;

  const hasArea    = breakdown.some(s => s.type === "area");
  const hasManHour = breakdown.some(s => s.type === "manhour");

  const toggle = (id) =>
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const abbr = UNITS.find(u => u.value === unit)?.abbr || "ft";

  // Required-field hints: show when one side of a pair is filled but the other is empty
  const needWidth   = length !== "" && width   === "";
  const needLength  = width  !== "" && length  === "";
  const needHours   = workers !== "" && hours  === "";
  const needWorkers = hours  !== "" && workers === "";

  return (
    <section className="calc">
      {/* floating particles */}
      <div className="calc__particles" aria-hidden="true">
        {[...Array(6)].map((_, i) => (
          <span key={i} className={`calc__particle calc__particle--${i + 1}`} />
        ))}
      </div>

      {/* ── HEADER ── */}
      <div className="calc__header">
        <span className="calc__tag">✦ Transparent Pricing</span>
        <h1 className="calc__title">
          Garden Service{" "}
          <em className="calc__title-em">Price Calculator</em>
        </h1>
        <p className="calc__subtitle">
          Select services, enter your garden dimensions, and get an instant
          cost estimate — no hidden charges.
        </p>
        <span className="calc__divider" />
      </div>

      {/* ── BODY ── */}
      <div className="calc__body">

        {/* ── LEFT: SERVICE PICKER ── */}
        <div className="calc__left">

          {/* Search */}
          <div className="calc__search-wrap">
            <span className="calc__search-icon">🔍</span>
            <input
              className="calc__search"
              type="text"
              placeholder="Search…  e.g. grass, khaad, pani"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
            {search && (
              <button className="calc__search-clear" onClick={() => setSearch("")}>✕</button>
            )}
          </div>

          {/* Service list */}
          <div className="calc__svc-list">
            {filtered.map(s => (
              <button
                key={s.id}
                className={`calc__svc${selected.has(s.id) ? " calc__svc--on" : ""}`}
                onClick={() => toggle(s.id)}
              >
                <span className="calc__svc-icon">{s.icon}</span>
                <div className="calc__svc-info">
                  <span className="calc__svc-name">{s.name}</span>
                  <span className="calc__svc-hindi">{s.hindi}</span>
                  <span className="calc__svc-desc">{s.desc}</span>
                </div>
                <div className="calc__svc-meta">
                  <span className="calc__svc-rate">
                    {s.type === "area"
                      ? `₹${s.rate}/sq ft`
                      : `₹${MAN_FIRST_HOUR} + ₹${MAN_EXTRA_RATE}/hr after`}
                  </span>
                  <span className="calc__svc-badge">
                    {s.type === "area" ? "Area" : "Labour"}
                  </span>
                  <span className={`calc__svc-tick${selected.has(s.id) ? " calc__svc-tick--on" : ""}`}>
                    {selected.has(s.id) ? "✓" : "+"}
                  </span>
                </div>
              </button>
            ))}
            {filtered.length === 0 && (
              <p className="calc__no-results">No services found for "{search}"</p>
            )}
          </div>
        </div>

        {/* ── RIGHT: INPUTS + BREAKDOWN ── */}
        <div className="calc__right">
          <p className="calc__section-label calc__section-label--right">Configure &amp; Estimate</p>

          {/* Area input — show when area services are selected, or nothing selected yet */}
          {(hasArea || selected.size === 0) && (
            <div className="calc__panel">
              <h3 className="calc__panel-title">📐 Garden Dimensions</h3>

              <div className="calc__unit-row">
                {UNITS.map(u => (
                  <button
                    key={u.value}
                    className={`calc__unit-btn${unit === u.value ? " calc__unit-btn--on" : ""}`}
                    onClick={() => setUnit(u.value)}
                  >
                    {u.label}
                  </button>
                ))}
              </div>

              <div className="calc__dim-row">
                <div className="calc__dim-field">
                  <label className="calc__dim-label">
                    Length ({abbr}){needLength && <span className="calc__required"> *required</span>}
                  </label>
                  <input
                    className={`calc__dim-input${needLength ? " calc__dim-input--req" : ""}`}
                    type="number"
                    min="0"
                    value={length}
                    onChange={e => setLength(e.target.value)}
                    placeholder="e.g. 50"
                  />
                </div>
                <span className="calc__dim-x">×</span>
                <div className="calc__dim-field">
                  <label className="calc__dim-label">
                    Width ({abbr}){needWidth && <span className="calc__required"> *required</span>}
                  </label>
                  <input
                    className={`calc__dim-input${needWidth ? " calc__dim-input--req" : ""}`}
                    type="number"
                    min="0"
                    value={width}
                    onChange={e => setWidth(e.target.value)}
                    placeholder="e.g. 30"
                  />
                </div>
              </div>

              {sqFt > 0 && (
                <p className="calc__area-result">
                  Area = <strong>{fmt(sqFt)} sq ft</strong>
                  {unit !== "sqft" && (
                    <span className="calc__area-note">
                      &nbsp;({parseFloat(length)||0} × {parseFloat(width)||0} {unit === "meter" ? "sq m" : "sq gaj"})
                    </span>
                  )}
                </p>
              )}
            </div>
          )}

          {/* Labour input */}
          {hasManHour && (
            <div className="calc__panel">
              <h3 className="calc__panel-title">👷 Labour Details</h3>
              <div className="calc__labour-rates">
                <span>1st hr: <strong>₹{MAN_FIRST_HOUR}/man</strong></span>
                <span className="calc__labour-dot">·</span>
                <span>From 2nd hr: <strong>₹{MAN_EXTRA_RATE}/man/hr</strong></span>
              </div>
              <div className="calc__dim-row">
                <div className="calc__dim-field">
                  <label className="calc__dim-label">
                    Workers{needWorkers && <span className="calc__required"> *required</span>}
                  </label>
                  <input
                    className={`calc__dim-input${needWorkers ? " calc__dim-input--req" : ""}`}
                    type="number"
                    min="1"
                    value={workers}
                    onChange={e => setWorkers(e.target.value)}
                    placeholder="e.g. 2"
                  />
                </div>
                <span className="calc__dim-x">×</span>
                <div className="calc__dim-field">
                  <label className="calc__dim-label">
                    Hours{needHours && <span className="calc__required"> *required</span>}
                  </label>
                  <input
                    className={`calc__dim-input${needHours ? " calc__dim-input--req" : ""}`}
                    type="number"
                    min="1"
                    value={hours}
                    onChange={e => setHours(e.target.value)}
                    placeholder="e.g. 3"
                  />
                </div>
              </div>
              {workers !== "" && hours !== "" && manHourCost > 0 && (
                <p className="calc__area-result">
                  = <strong>₹{fmt(manHourCost)}</strong> per labour service
                  <span className="calc__area-note">
                    &nbsp;({workers} {parseInt(workers) === 1 ? "worker" : "workers"}, {hours} {parseInt(hours) === 1 ? "hr" : "hrs"})
                  </span>
                </p>
              )}
            </div>
          )}

          {/* Price breakdown */}
          {breakdown.length > 0 && (
            <div className="calc__panel calc__panel--breakdown">
              <h3 className="calc__panel-title">💰 Price Breakdown</h3>
              <div className="calc__breakdown">

                {breakdown.map(s => (
                  <div key={s.id} className="calc__row">
                    <span className="calc__row-name">{s.icon} {s.name}</span>
                    <span className="calc__row-cost">₹{fmt(s.cost)}</span>
                  </div>
                ))}

                {belowMinimum && (
                  <div className="calc__min-warning">
                    <span className="calc__min-warning-icon">⚠️</span>
                    <div>
                      <strong>Below minimum order of ₹{fmt(MIN_ORDER_VALUE)}</strong>
                      <p>Add more services or ₹{fmt(MIN_ORDER_VALUE)} minimum applies.</p>
                    </div>
                  </div>
                )}

                {/* Billing-style total line */}
                <div className="calc__total-row">
                  <span className="calc__total-label">
                    {belowMinimum ? "Minimum Payable" : "Total Estimate"}
                  </span>
                  <span className="calc__total-amount">₹{fmt(payable)}</span>
                </div>

                <p className="calc__disclaimer">
                  * Approximate estimate. Final price may vary based on site conditions.
                </p>
              </div>
              <button className="calc__cta" onClick={openPopup}>
                Get Exact Quote ✦
              </button>
            </div>
          )}

          {/* Empty state */}
          {selected.size === 0 && (
            <div className="calc__empty-state">
              <span className="calc__empty-icon">🌿</span>
              <p>Select services on the left to see your estimate</p>
            </div>
          )}
        </div>
      </div>

      {/* ── QUOTE POPUP ── */}
      {quoteOpen && (
        <div className="qpop__overlay" onClick={closePopup}>
          <div className="qpop" onClick={e => e.stopPropagation()}>

            <button className="qpop__close" onClick={closePopup}>✕</button>

            {/* ── STEP 1: action buttons ── */}
            {popStep === "actions" && (
              <>
                <div className="qpop__header">
                  <span className="qpop__icon">🌿</span>
                  <h2 className="qpop__title">Get Your Exact Quote</h2>
                  <p className="qpop__sub">
                    Estimate: <strong>₹{fmt(payable)}</strong>
                    {belowMinimum && <span className="qpop__min-note"> (min. order applies)</span>}
                  </p>
                </div>

                <div className="qpop__actions">
                  <a href={`tel:+${PHONE}`} className="qpop__btn qpop__btn--call">
                    <span className="qpop__btn-icon">📞</span>
                    <div className="qpop__btn-text">
                      <span className="qpop__btn-label">Call Us</span>
                      <span className="qpop__btn-detail">+91-98000-40505</span>
                    </div>
                  </a>

                  <a
                    href={buildWhatsApp(breakdown, sqFt, workers, hours, payable)}
                    target="_blank" rel="noopener noreferrer"
                    className="qpop__btn qpop__btn--whatsapp"
                  >
                    <span className="qpop__btn-icon">💬</span>
                    <div className="qpop__btn-text">
                      <span className="qpop__btn-label">WhatsApp</span>
                      <span className="qpop__btn-detail">Chat with us directly</span>
                    </div>
                  </a>

                  <button
                    className="qpop__btn qpop__btn--email"
                    onClick={() => setPopStep("form")}
                  >
                    <span className="qpop__btn-icon">✉️</span>
                    <div className="qpop__btn-text">
                      <span className="qpop__btn-label">Send Query</span>
                      <span className="qpop__btn-detail">We'll email you a final quote</span>
                    </div>
                  </button>
                </div>

                <p className="qpop__note">Typically respond within 2–4 hrs on business days.</p>
              </>
            )}

            {/* ── STEP 2: mini contact form ── */}
            {popStep === "form" && (
              <>
                <div className="qpop__header">
                  <span className="qpop__icon">✉️</span>
                  <h2 className="qpop__title">Send Your Query</h2>
                  <p className="qpop__sub">Estimate details will be sent automatically.</p>
                </div>

                <div className="qpop__form">
                  <div className="qpop__field">
                    <label className="qpop__label">Your Name *</label>
                    <input
                      className={`qpop__input${formErr.name ? " qpop__input--err" : ""}`}
                      type="text"
                      placeholder="Full name"
                      value={formData.name}
                      onChange={e => { setFormData(p => ({...p, name: e.target.value})); setFormErr(p => ({...p, name: ""})); }}
                    />
                    {formErr.name && <span className="qpop__err">{formErr.name}</span>}
                  </div>

                  <div className="qpop__field">
                    <label className="qpop__label">Email Address *</label>
                    <input
                      className={`qpop__input${formErr.email ? " qpop__input--err" : ""}`}
                      type="email"
                      placeholder="your@email.com"
                      value={formData.email}
                      onChange={e => { setFormData(p => ({...p, email: e.target.value})); setFormErr(p => ({...p, email: ""})); }}
                    />
                    {formErr.email && <span className="qpop__err">{formErr.email}</span>}
                  </div>

                  <div className="qpop__field">
                    <label className="qpop__label">Phone Number *</label>
                    <input
                      className={`qpop__input${formErr.phone ? " qpop__input--err" : ""}`}
                      type="tel"
                      placeholder="+91 XXXXX XXXXX"
                      value={formData.phone}
                      onChange={e => { setFormData(p => ({...p, phone: e.target.value})); setFormErr(p => ({...p, phone: ""})); }}
                    />
                    {formErr.phone && <span className="qpop__err">{formErr.phone}</span>}
                  </div>

                  <div className="qpop__form-actions">
                    <button className="qpop__back" onClick={() => setPopStep("actions")}>← Back</button>
                    <button
                      className="qpop__submit"
                      onClick={sendQuery}
                      disabled={submitting}
                    >
                      {submitting ? "Sending…" : "Send Query ✦"}
                    </button>
                  </div>
                </div>
              </>
            )}

            {/* ── STEP 3: success ── */}
            {popStep === "success" && (
              <div className="qpop__result">
                <span className="qpop__result-icon">✅</span>
                <h2 className="qpop__title">Query Sent!</h2>
                <p className="qpop__sub">We've received your estimate request and will reply within 2–4 hours.</p>
                <button className="qpop__submit" style={{marginTop:"20px"}} onClick={closePopup}>Close</button>
              </div>
            )}

            {/* ── STEP 3b: error ── */}
            {popStep === "error" && (
              <div className="qpop__result">
                <span className="qpop__result-icon">⚠️</span>
                <h2 className="qpop__title">Could Not Send</h2>
                <p className="qpop__sub">Please try WhatsApp or call us directly at +91-9800040505.</p>
                <button className="qpop__back" style={{marginTop:"16px"}} onClick={() => setPopStep("actions")}>← Try Again</button>
              </div>
            )}

          </div>
        </div>
      )}

    </section>
  );
}
